import { useState, useEffect, createContext, useContext } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Card, Table, Button, Space, Switch, Tooltip, Typography } from 'antd';
import { EditFilled, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { APIClient } from '../../helpers/api_helper';
import Breadcrumb from '../../Common/Breadcrumb';
import TagRuleDrawer from '../../Common/TagRuleDrawer';
import { setInitialFilterConf } from '../../slices/filters/thunk';
import { loadTagRules } from '../../slices/thunk';

const api = new APIClient();

// Lets the handle column reach its own row's drag listeners without spreading
// them over the whole <tr>, which would make the Edit button and the Aktivno
// switch fight the drag sensor.
const DragHandleContext = createContext(null);

const DragHandle = () => {
  const handle = useContext(DragHandleContext);
  if (!handle) {
    return <HolderOutlined style={{ cursor: 'not-allowed', opacity: 0.3 }} />;
  }
  return (
    <HolderOutlined
      style={{ cursor: 'move' }}
      ref={handle.setActivatorNodeRef}
      {...handle.listeners}
      {...handle.attributes}
    />
  );
};

const SortableRow = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    // antd hands data-row-key down as a STRING while rowKey returns a number,
    // so both this id and the SortableContext items must be strings -- otherwise
    // dnd-kit matches nothing and the drag silently does nothing at all.
    useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9 } : {}),
  };

  return (
    <DragHandleContext.Provider value={{ setActivatorNodeRef, listeners, attributes }}>
      <tr {...props} ref={setNodeRef} style={style} />
    </DragHandleContext.Provider>
  );
};


const TagRulesList = () => {
  document.title = "Pravila za oznake" + process.env.REACT_APP_PAGE_TITLE;

  const dispatch = useDispatch();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const filterdata = useSelector((state) => state.Filterdata.data)
  const searchPending = useSelector((state) => state.Filterdata.searchPending)
  const [editedId, setEditedId] = useState(null);
  const [open, setOpen] = useState(false);

  // Reordering sends the org's COMPLETE rule list, which a filtered table does
  // not have -- the server rejects a partial list rather than half-renumber.
  const searching = !!filterdata.search;

  const sensors = useSensors(
    // Without a distance threshold a plain click on the handle starts a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    dispatch(setInitialFilterConf({
      data: {},
      // Only the normal search box.
      hidden: ['relateds_group', 'intent_type', 'intent_checkboxes_group', 'dt_range'],
      disabled: []
    }))
  }, []);

  const loadRules = () => {
    setLoading(true);
    api.get('/api/tag_rules/', filterdata)
      // 403 resolves to null rather than rejecting (see api_helper), so guard
      // or a permission error blanks the table with no explanation.
      .then((resp) => setRules(Array.isArray(resp) ? resp : []))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadRules() }, [filterdata]);

  const onEdit = (id) => {
    setEditedId(id)
    setOpen(true);
  }

  const toggleActive = (rule, is_active) => {
    // Optimistic: the switch is the one control people flick repeatedly, and a
    // round-trip before it moves feels broken.
    setRules(rules.map(r => (r.id === rule.id ? { ...r, is_active } : r)));
    api.update(`/api/tag_rules/${rule.id}/`, { is_active }, { successMsg: null })
      .then(() => dispatch(loadTagRules()))
      .catch(() => loadRules());
  }

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = rules.findIndex((r) => String(r.id) === active.id);
    const to = rules.findIndex((r) => String(r.id) === over.id);
    if (from < 0 || to < 0) return;

    const next = arrayMove(rules, from, to);
    setRules(next);
    api.update(
      '/api/tag_rules/reorder/',
      { ids: next.map((r) => r.id) },
      { successMsg: 'Vrstni red pravil posodobljen' }
    )
      .then((resp) => {
        if (Array.isArray(resp)) setRules(resp);
        dispatch(loadTagRules());
      })
      .catch(() => loadRules());  // server refused: refetch the truth
  };

  const columns = [
    {
      title: 'Vrstni red',
      key: 'order',
      width: 110,
      render: (_, __, index) => (
        <Space size={8}>
          <Tooltip title={searching ? 'Za razvrščanje počistite iskanje.' : 'Povleci za razvrščanje'}>
            <span><DragHandle /></span>
          </Tooltip>
          <span>{index + 1}</span>
        </Space>
      ),
    },
    {
      title: 'Vzorec',
      dataIndex: 'pattern',
      // The rename rides along under the pattern rather than in a column of its
      // own: only about one rule in seven sets it, so a dedicated column would
      // be mostly empty for the sake of a handful of rows.
      render: (pattern, r) => (
        <Space direction="vertical" size={0}>
          <span>{pattern}</span>
          {r.title_override && (
            <Tooltip title="Ob uporabi pravila se naziv zamenja s tem besedilom">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {`→ ${r.title_override}`}
              </Typography.Text>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Oznaka',
      dataIndex: 'tag',
      render: (_, r) => (
        <Space size={6}>
          <span style={{
            display: 'inline-block', width: 10, height: 10,
            borderRadius: 2, background: r.tag_color_hex || '#808080'
          }} />
          {r.tag_path}
        </Space>
      ),
    },
    {
      title: 'Aktivno',
      dataIndex: 'is_active',
      width: 90,
      render: (is_active, r) => (
        <Switch size="small" checked={is_active} onChange={(v) => toggleActive(r, v)} />
      ),
    },
    {
      title: 'Uredi',
      key: 'edit',
      width: 70,
      render: r => <Button size='small' onClick={ev => onEdit(r.id)}><EditFilled/></Button>,
    },
  ]

  const table = (
    <Table
      size="small"
      loading={loading || searchPending}
      columns={columns}
      dataSource={rules}
      pagination={false}
      rowKey={obj => String(obj.id)}
      // Unpaginated by design (the endpoint has pagination_class = None): you
      // cannot drag a row from page 2 onto page 1.
      components={searching ? undefined : { body: { row: SortableRow } }}
    />
  );

  return (
    <>
      <TagRuleDrawer
        open={open}
        id={editedId}
        onCancel={() => {
          setOpen(false);
          setEditedId(null);
        }}
        onDone={() => {
          loadRules();
          setEditedId(null);
          setOpen(false);
        }}
      />

      <Breadcrumb parentTitle="Statični podatki" pageTitle="Pravila za oznake" />

      <Card
        title='Pravila za oznake'
        extra={
          <Space>
            <Button
              onClick={ev => {
                setEditedId(null)
                setOpen(true)
              }}
              icon={<PlusOutlined />}
            />
          </Space>
        }
      >
        <p style={{ color: '#888', marginTop: 0 }}>
          Ko v transakciji vnesete naziv, se oznaka samodejno predlaga po prvem
          pravilu, katerega vzorec je vsebovan v nazivu. Velike in male črke ter
          šumniki niso pomembni. Vrstni red določa, katero pravilo velja, če se
          jih ujema več.
        </p>

        {searching ? table : (
          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <SortableContext
              items={rules.map((r) => String(r.id))}
              strategy={verticalListSortingStrategy}
            >
              {table}
            </SortableContext>
          </DndContext>
        )}
      </Card>
    </>
  )
}

export default TagRulesList;
